import { useEffect, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

export const isValidPageParam = (pageValue: string | null): boolean => {
  if (!pageValue) return true;

  if (!/^[1-9]\d*$/.test(pageValue)) {
    return false;
  }

  const num = parseInt(pageValue, 10);
  return !isNaN(num) && num > 0 && num <= Number.MAX_SAFE_INTEGER;
};

type UseSanitizedPageParamOptions = {
  basePath: string;
};

type UseSanitizedPageParamReturn = {
  isValid: boolean;
  pageValue: number | null;
};

export const useSanitizedPageParam = (options: UseSanitizedPageParamOptions): UseSanitizedPageParamReturn => {
  const { basePath } = options;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const pageValue = searchParams.get("page");

  const validationResult = useMemo(() => {
    if (!pageValue) {
      return { isValid: true, pageValue: null };
    }

    if (!isValidPageParam(pageValue)) {
      return { isValid: false, pageValue: null };
    }

    return { isValid: true, pageValue: parseInt(pageValue, 10) };
  }, [pageValue]);

  useEffect(() => {
    if (!basePath) return;

    if (!validationResult.isValid && pageValue) {
      const params = new URLSearchParams(searchParams);
      params.delete("page");

      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath;

      navigate(newUrl, { replace: true });
    }
  }, [validationResult.isValid, pageValue, basePath, searchParams, navigate]);

  return {
    isValid: validationResult.isValid,
    pageValue: validationResult.pageValue,
  };
};
